/**
 * FormFieldsBuilder Component
 * 
 * Builder for configuring dynamic form fields on the Amount Selection page.
 * MVP: Supports radio button groups for LoreNote-style rewards.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Input, Select } from '@/components/common/FormField';
import type { FormFieldDefinition, FormFieldType, FormFieldWidth } from '@/app/types/rewards';

import './FormFieldsBuilder.scss';

interface FormFieldsBuilderProps {
  value: FormFieldDefinition[] | undefined;
  onChange: (fields: FormFieldDefinition[] | undefined) => void;
}

const FIELD_TYPE_OPTIONS = [
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email Input' },
  { value: 'phone', label: 'Phone Input' },
  { value: 'date', label: 'Date Picker' },
  { value: 'checkbox_group', label: 'Checkbox Group' },
];

const DATE_CONSTRAINT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'today', label: 'Today (dynamic)' },
  { value: 'static', label: 'Static Date...' },
];

const FIELD_WIDTH_OPTIONS = [
  { value: 'full', label: 'Full Width' },
  { value: 'half', label: 'Half Width' },
  { value: 'third', label: 'Third Width' },
  { value: 'quarter', label: 'Quarter Width' },
];

const VALIDATION_PRESET_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'zipcode', label: 'US Zip Code (5 or 9 digit)' },
  { value: 'usstate', label: 'US State Code (2-letter)' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'email', label: 'Email Address' },
  { value: 'date_mmddyyyy', label: 'Date (MM/DD/YYYY)' },
  { value: 'incomm_text', label: 'InComm Text (prohibited chars + max length)' },
  { value: 'custom', label: 'Custom Pattern' },
];

/** REW-1137/REW-1138: InComm SFTP prohibited characters — apostrophe and dash allowed. */
export const INCOMM_TEXT_PATTERN = '^[^<()"\\\\+]+$';

export const INCOMM_TEXT_CUSTOM_MESSAGE =
  "This field contains characters that aren't accepted for card mailing";

/** Authoring defaults only — stamped into catalog JSON; MFE/service read saved validation at runtime. */
export const INCOMM_FIELD_MAX_LENGTHS: Readonly<Record<string, number>> = {
  firstname: 32,
  lastname: 32,
  address1: 35,
  address2: 35,
  city: 32,
};

export function resolveIncommFieldMaxLength(fieldId: string): number | undefined {
  return INCOMM_FIELD_MAX_LENGTHS[fieldId];
}

const VALIDATION_PRESETS: Record<string, { pattern: string; customMessage: string }> = {
  zipcode: { pattern: '^\\d{5}(-\\d{4})?$', customMessage: 'Please enter a valid zip code' },
  usstate: { 
    pattern: '^([Aa][Ll]|[Aa][Kk]|[Aa][Zz]|[Aa][Rr]|[Cc][Aa]|[Cc][Oo]|[Cc][Tt]|[Dd][Ee]|[Dd][Cc]|[Ff][Ll]|[Gg][Aa]|[Hh][Ii]|[Ii][Dd]|[Ii][Ll]|[Ii][Nn]|[Ii][Aa]|[Kk][Ss]|[Kk][Yy]|[Ll][Aa]|[Mm][Ee]|[Mm][Dd]|[Mm][Aa]|[Mm][Ii]|[Mm][Nn]|[Mm][Ss]|[Mm][Oo]|[Mm][Tt]|[Nn][Ee]|[Nn][Vv]|[Nn][Hh]|[Nn][Jj]|[Nn][Mm]|[Nn][Yy]|[Nn][Cc]|[Nn][Dd]|[Oo][Hh]|[Oo][Kk]|[Oo][Rr]|[Pp][Aa]|[Rr][Ii]|[Ss][Cc]|[Ss][Dd]|[Tt][Nn]|[Tt][Xx]|[Uu][Tt]|[Vv][Tt]|[Vv][Aa]|[Ww][Aa]|[Ww][Vv]|[Ww][Ii]|[Ww][Yy])$', 
    customMessage: 'Please enter a valid 2-letter US state code' 
  },
  phone: { pattern: '^[\\d\\s\\-\\(\\)\\+]+$', customMessage: 'Please enter a valid phone number' },
  email: { pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', customMessage: 'Please enter a valid email address' },
  date_mmddyyyy: { pattern: '^(0[1-9]|1[0-2])\\/(0[1-9]|[12]\\d|3[01])\\/\\d{4}$', customMessage: 'Please enter a valid date in MM/DD/YYYY format' },
  incomm_text: { pattern: INCOMM_TEXT_PATTERN, customMessage: INCOMM_TEXT_CUSTOM_MESSAGE },
};

const VALIDATION_PRESET_PATTERNS = Object.fromEntries(
  Object.entries(VALIDATION_PRESETS).map(([key, preset]) => [key, preset.pattern])
) as Record<string, string>;

type FieldValidation = NonNullable<FormFieldDefinition['validation']>;

export function resolveValidationPresetSelectValue(
  validation: FieldValidation | undefined,
  presetPatterns: Record<string, string> = VALIDATION_PRESET_PATTERNS,
): string {
  if (!validation) {
    return '';
  }
  if (validation.pattern) {
    const presetKey = Object.entries(presetPatterns).find(([, pattern]) => pattern === validation.pattern)?.[0];
    return presetKey ?? 'custom';
  }
  if ('pattern' in validation) {
    return 'custom';
  }
  return '';
}

export function shouldShowValidationPatternEditor(
  validation: FieldValidation | undefined,
): boolean {
  if (!validation) {
    return false;
  }
  return Boolean(validation.pattern) || 'pattern' in validation;
}

const INPUT_MODE_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'text', label: 'Text' },
  { value: 'numeric', label: 'Numeric (0-9)' },
  { value: 'tel', label: 'Telephone' },
  { value: 'email', label: 'Email' },
  { value: 'decimal', label: 'Decimal' },
];

const createEmptyField = (): FormFieldDefinition => ({
  id: `field_${Date.now()}`,
  type: 'text',
  label: '',
  required: true,
});

export const FormFieldsBuilder: React.FC<FormFieldsBuilderProps> = ({ value, onChange }) => {
  const [expandedFieldIndex, setExpandedFieldIndex] = useState<number | null>(null);
  const fields = useMemo(() => value || [], [value]);

  // Add a new field
  const handleAddField = useCallback(() => {
    const newField = createEmptyField();
    const newFields = [...fields, newField];
    onChange(newFields);
    setExpandedFieldIndex(newFields.length - 1);
  }, [fields, onChange]);

  // Remove a field
  const handleRemoveField = useCallback((index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    onChange(newFields.length > 0 ? newFields : undefined);
    setExpandedFieldIndex(null);
  }, [fields, onChange]);

  // Update a field property
  const updateField = useCallback((index: number, updates: Partial<FormFieldDefinition>) => {
    const newFields = fields.map((field, i) => 
      i === index ? { ...field, ...updates } : field
    );
    onChange(newFields);
  }, [fields, onChange]);

  // Move a field up or down
  const handleMoveField = useCallback((index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === fields.length - 1)
    ) {
      return;
    }

    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex] as FormFieldDefinition;
    newFields[targetIndex] = temp as FormFieldDefinition;
    
    onChange(newFields);
    if (expandedFieldIndex === index) {
      setExpandedFieldIndex(targetIndex);
    } else if (expandedFieldIndex === targetIndex) {
      setExpandedFieldIndex(index);
    }
  }, [fields, onChange, expandedFieldIndex]);

  // Check if field type needs options
  const needsOptions = (type: FormFieldType): boolean => {
    return ['radio', 'select', 'checkbox_group'].includes(type);
  };

  return (
    <div className="form-fields-builder">
      <div className="form-fields-builder__header">
        <h4>Dynamic Form Fields</h4>
        <p className="form-fields-builder__description">
          Configure form fields that users must fill out before submitting their redemption.
        </p>
      </div>

      {fields.length === 0 ? (
        <div className="form-fields-builder__empty">
          <p>No form fields configured. Click below to add a field.</p>
        </div>
      ) : (
        <div className="form-fields-builder__list">
          {fields.map((field, fieldIndex) => (
            <div 
              key={fieldIndex} 
              className={`form-fields-builder__field ${expandedFieldIndex === fieldIndex ? 'expanded' : ''}`}
            >
              {/* Field Header */}
              <div 
                className="form-fields-builder__field-header"
                onClick={() => setExpandedFieldIndex(expandedFieldIndex === fieldIndex ? null : fieldIndex)}
              >
                <div className="form-fields-builder__field-summary">
                  <span className="form-fields-builder__field-type">{field.type}</span>
                  <code className="form-fields-builder__field-id">{field.id || 'no-id'}</code>
                  {field.required && <span className="form-fields-builder__required-badge">*</span>}
                  {field.width && field.width !== 'full' && (
                    <span className="form-fields-builder__width-badge">{field.width}</span>
                  )}
                  {field.label && (
                    <span className="form-fields-builder__field-label">— {field.label}</span>
                  )}
                  {field.options && field.options.length > 0 && (
                    <span className="form-fields-builder__options-count">
                      ({field.options.length} options)
                    </span>
                  )}
                </div>
                <div className="form-fields-builder__field-actions">
                  <button
                    type="button"
                    className="btn-move"
                    disabled={fieldIndex === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveField(fieldIndex, 'up');
                    }}
                    title="Move Up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-move"
                    disabled={fieldIndex === fields.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveField(fieldIndex, 'down');
                    }}
                    title="Move Down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveField(fieldIndex);
                    }}
                    title="Remove field"
                  >
                    ×
                  </button>
                  <span className="expand-icon">{expandedFieldIndex === fieldIndex ? '▼' : '▶'}</span>
                </div>
              </div>

              {/* Field Editor (expanded) */}
              {expandedFieldIndex === fieldIndex && (
                <div className="form-fields-builder__field-editor">
                  <div className="form-fields-builder__field-row">
                    <Input
                      label="Field ID"
                      value={field.id}
                      onChange={(val) => updateField(fieldIndex, { id: val })}
                      placeholder="e.g., delivery_method"
                      hint="Unique identifier sent to backend"
                    />
                    <Select
                      label="Field Type"
                      value={field.type}
                      onChange={(val) => {
                        const newType = val as FormFieldType;
                        const updates: Partial<FormFieldDefinition> = { type: newType };
                        if (!needsOptions(newType)) updates.options = undefined;
                        if (newType !== 'date') {
                          updates.minDate = undefined;
                          updates.maxDate = undefined;
                        }
                        updateField(fieldIndex, updates);
                      }}
                      options={FIELD_TYPE_OPTIONS}
                    />
                    <Select
                      label="Width"
                      value={field.width || 'full'}
                      onChange={(val) => updateField(fieldIndex, { 
                        width: val === 'full' ? undefined : val as FormFieldWidth
                      })}
                      options={FIELD_WIDTH_OPTIONS}
                    />
                  </div>

                  <Input
                    label="Label"
                    value={field.label}
                    onChange={(val) => updateField(fieldIndex, { label: val })}
                    placeholder="e.g., Receive my unique link and code via:"
                  />

                  <div className="form-fields-builder__field-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(fieldIndex, { required: e.target.checked })}
                      />
                      Required field
                    </label>
                  </div>

                  <Input
                    label="Placeholder (optional)"
                    value={field.placeholder || ''}
                    onChange={(val) => updateField(fieldIndex, { placeholder: val || undefined })}
                    placeholder="Placeholder text for text inputs"
                  />

                  <Input
                    label="Help Text (optional)"
                    value={field.helpText || ''}
                    onChange={(val) => updateField(fieldIndex, { helpText: val || undefined })}
                    placeholder="Additional help text shown below the field"
                  />

                  <Input
                    label="Default Value (optional)"
                    value={field.defaultValue || ''}
                    onChange={(val) => updateField(fieldIndex, { defaultValue: val || undefined })}
                    placeholder="e.g., {{first_name}} or manual value"
                    hint="Use template variables like {{first_name}} for auto-population"
                  />

                  {/* Validation Section (for text-based inputs) */}
                  {['text', 'email', 'phone'].includes(field.type) && (
                    <div className="form-fields-builder__validation">
                      <h5>Validation</h5>
                      <div className="form-fields-builder__field-row">
                        <Input
                          label="Min Length"
                          value={field.validation?.minLength?.toString() || ''}
                          onChange={(val) => {
                            const num = val ? parseInt(val, 10) : undefined;
                            const newValidation = { ...field.validation, minLength: (num && !isNaN(num)) ? num : undefined };
                            const hasAny = newValidation.pattern || newValidation.minLength || newValidation.maxLength || newValidation.customMessage;
                            updateField(fieldIndex, { validation: hasAny ? newValidation : undefined });
                          }}
                          placeholder="e.g., 4"
                        />
                        <Input
                          label="Max Length"
                          value={field.validation?.maxLength?.toString() || ''}
                          onChange={(val) => {
                            const num = val ? parseInt(val, 10) : undefined;
                            const newValidation = { ...field.validation, maxLength: (num && !isNaN(num)) ? num : undefined };
                            const hasAny = newValidation.pattern || newValidation.minLength || newValidation.maxLength || newValidation.customMessage;
                            updateField(fieldIndex, { validation: hasAny ? newValidation : undefined });
                          }}
                          placeholder="e.g., 32"
                        />
                      </div>
                      <Select
                        label="Validation Preset"
                        value={resolveValidationPresetSelectValue(field.validation)}
                        onChange={(val) => {
                          if (!val) {
                            const kept = { minLength: field.validation?.minLength, maxLength: field.validation?.maxLength };
                            const hasAny = kept.minLength || kept.maxLength;
                            updateField(fieldIndex, { validation: hasAny ? kept : undefined });
                          } else if (val === 'custom') {
                            updateField(fieldIndex, { 
                              validation: { 
                                ...field.validation,
                                pattern: field.validation?.pattern ?? '', 
                                customMessage: field.validation?.customMessage ?? '' 
                              } 
                            });
                          } else {
                            const preset = VALIDATION_PRESETS[val];
                            if (preset) {
                              const incommMaxLength =
                                val === 'incomm_text' ? resolveIncommFieldMaxLength(field.id) : undefined;
                              updateField(fieldIndex, { 
                                validation: { 
                                  ...field.validation,
                                  ...preset,
                                  ...(incommMaxLength !== undefined ? { maxLength: incommMaxLength } : {}),
                                } 
                              });
                            }
                          }
                        }}
                        options={VALIDATION_PRESET_OPTIONS}
                      />
                      {shouldShowValidationPatternEditor(field.validation) && (
                        <>
                          <Input
                            label="Pattern (regex)"
                            value={field.validation?.pattern ?? ''}
                            onChange={(val) => updateField(fieldIndex, {
                              validation: { ...(field.validation ?? {}), pattern: val },
                            })}
                            placeholder="e.g., ^\\d{5}$"
                          />
                          <Input
                            label="Error Message"
                            value={field.validation?.customMessage ?? ''}
                            onChange={(val) => updateField(fieldIndex, {
                              validation: { ...(field.validation ?? {}), customMessage: val || undefined },
                            })}
                            placeholder="e.g., Please enter a valid zip code"
                          />
                        </>
                      )}
                    </div>
                  )}

                  {/* Keyboard Type (for text inputs — phone/email auto-derive in MFE) */}
                  {field.type === 'text' && (
                    <Select
                      label="Keyboard Type (mobile)"
                      value={field.inputMode || ''}
                      onChange={(val) => updateField(fieldIndex, { inputMode: (val as FormFieldDefinition['inputMode']) || undefined })}
                      options={INPUT_MODE_OPTIONS}
                    />
                  )}

                  {/* Date Constraints (for date fields) */}
                  {field.type === 'date' && (
                    <div className="form-fields-builder__validation">
                      <h5>Date Constraints</h5>
                      <div className="form-fields-builder__field-row">
                        <div>
                          <Select
                            label="Min Date"
                            value={
                              !field.minDate ? '' 
                              : field.minDate === 'today' ? 'today' 
                              : 'static'
                            }
                            onChange={(val) => {
                              if (!val) {
                                updateField(fieldIndex, { minDate: undefined });
                              } else if (val === 'today') {
                                updateField(fieldIndex, { minDate: 'today' });
                              } else {
                                updateField(fieldIndex, { minDate: field.minDate === 'today' ? '' : (field.minDate || '') });
                              }
                            }}
                            options={DATE_CONSTRAINT_OPTIONS}
                          />
                          {field.minDate !== undefined && field.minDate !== 'today' && field.minDate !== undefined && (
                            <Input
                              label="Min Date (YYYY-MM-DD)"
                              value={field.minDate || ''}
                              onChange={(val) => updateField(fieldIndex, { minDate: val || undefined })}
                              placeholder="e.g., 1900-01-01"
                            />
                          )}
                        </div>
                        <div>
                          <Select
                            label="Max Date"
                            value={
                              !field.maxDate ? '' 
                              : field.maxDate === 'today' ? 'today' 
                              : 'static'
                            }
                            onChange={(val) => {
                              if (!val) {
                                updateField(fieldIndex, { maxDate: undefined });
                              } else if (val === 'today') {
                                updateField(fieldIndex, { maxDate: 'today' });
                              } else {
                                updateField(fieldIndex, { maxDate: field.maxDate === 'today' ? '' : (field.maxDate || '') });
                              }
                            }}
                            options={DATE_CONSTRAINT_OPTIONS}
                          />
                          {field.maxDate !== undefined && field.maxDate !== 'today' && (
                            <Input
                              label="Max Date (YYYY-MM-DD)"
                              value={field.maxDate || ''}
                              onChange={(val) => updateField(fieldIndex, { maxDate: val || undefined })}
                              placeholder="e.g., 2030-12-31"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Options Editor (for radio/select/checkbox_group) */}
                  {needsOptions(field.type) && (
                    <div className="form-fields-builder__options">
                      <h5>Options</h5>
                      <p className="form-fields-builder__options-hint">
                        Enter options as comma-separated values (e.g., "AL, AK, AZ, AR, CA, ..."). 
                        Value and label will be the same.
                      </p>
                      <textarea
                        className="form-fields-builder__options-textarea"
                        value={(field.options || []).map(o => o.value).join(', ')}
                        onChange={(e) => {
                          const values = e.target.value
                            .split(',')
                            .map(v => v.trim())
                            .filter(v => v.length > 0);
                          const newOptions = values.map(v => ({ value: v, label: v }));
                          updateField(fieldIndex, { options: newOptions.length > 0 ? newOptions : undefined });
                        }}
                        placeholder="e.g., AL, AK, AZ, AR, CA, CO, CT, DE, FL, GA..."
                        rows={4}
                      />
                      <p className="form-fields-builder__options-count">
                        {(field.options || []).length} option(s) configured
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="btn-add-field"
        onClick={handleAddField}
      >
        + Add Form Field
      </button>
    </div>
  );
};

export default FormFieldsBuilder;
